import { useCallback, useEffect, useRef, useState } from 'react';
import { HubConnectionBuilder } from '@microsoft/signalr';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCurrentAppVersion,
  getLatestAppVersion,
  setAppUpdateAvailable,
  setManagerReady,
  setManagerApiToken,
  setManagerTransportConnected,
} from '../redux/actions/desktopApp/desktopApp.actions';
import {
  currentGIMmanagerversionSelector,
  LatestGIMmanagerversionSelector,
  managerApiTokenSelector,
  managerReadySelector,
} from '../redux/selectors/desktopApp.selector';
import { selectIsAuthenticated } from '../redux/selectors/auth.selector';
import { userIdSelector } from '../redux/selectors/user.selector';
import { PLUGIN_HOST } from '../shared/config/globalConfig';
import { logDebug, logTrace } from '../shared/config/globalFunctions';
import {
  setHideInstallButton,
  updateStoreFileList,
  updateStoreGimProjects,
} from '../redux/actions/user/user.actions';
import {
  getManagerPairingStatus,
  initializeConnection,
  pairManager,
} from '../services/desktopApp/desktopApp.version.service';
import { issueManagerPairingCode } from '../services/webAPI/managerPairing.service';
import {
  UpdateObjectDownloadProgress,
  UpdateObjectUploadProgress,
} from '../redux/actions/object/object.actions';
import { GIMevents } from '../services/desktopApp/desktopApp.events';
import { compareVersion } from '@/shared/helper';

/** @typedef {import('../../global').ObjectUploadProgressEvent} ObjectUploadProgressEvent */
/** @typedef {import('../../global').ObjectDownloadProgressEvent} ObjectDownloadProgressEvent */

const MANAGER_RETRY_INTERVAL_MS = 10000;
// Module-level ownership prevents duplicate SignalR connections across hook instances.
let activeConnection = null;
let connectionStartPromise = null;

/** @typedef {{deviceId: string, status: string, isPaired?: boolean, credentialAvailable?: boolean, currentUserId?: number}} ManagerPairingStatus */

const DEFINITIVE_MANAGER_FAILURES = [400, 401, 403];

/**
 * @param {unknown} value candidate manager status
 * @returns {value is ManagerPairingStatus}
 */
const isManagerPairingStatus = (value) => {
  if (value === null || typeof value !== 'object') return false;
  const status = /** @type {Partial<ManagerPairingStatus>} */ (value);
  return (
    typeof status.deviceId === 'string' &&
    status.deviceId.length > 0 &&
    typeof status.status === 'string'
  );
};

/**
 * @param {ManagerPairingStatus} status manager status
 * @param {number} userId authenticated browser user id
 * @returns {boolean}
 */
const pairingRequired = (status, userId) =>
  status.isPaired !== true ||
  typeof userId !== 'number' ||
  userId <= 0 ||
  status.currentUserId !== userId;

/**
 * Owns one authenticated browser-to-manager connection.
 * @returns {{managerReady: boolean, connection: import('@microsoft/signalr').HubConnection|null, connectToDesktop: () => Promise<import('@microsoft/signalr').HubConnection|null>}}
 */
export function useGIMmanagerConnection() {
  // 1. Read browser authentication and manager version state.
  const dispatch = useDispatch();
  const managerReady = useSelector(managerReadySelector);
  const managerApiToken = useSelector(managerApiTokenSelector);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentVersion = useSelector(currentGIMmanagerversionSelector);
  const latestVersion = useSelector(LatestGIMmanagerversionSelector);

  // 2. Track SignalR transport and hook lifecycle.
  const [connection, setConnection] = useState(null);
  const retryIntervalRef = useRef(null);
  const mountedRef = useRef(false);
  const authReadyRef = useRef(isAuthenticated);

  // 3. Track targeted manager initialization.
  const initializedConnectionRef = useRef(null);
  const initializationRequestedRef = useRef(null);
  const initializationPromiseRef = useRef(null);

  // 4. Track single-flight pairing and pairing readiness.
  const pairingPromiseRef = useRef(null);
  const pairingReadyRef = useRef(false);

  // 5. Track capability delivery and connection cleanup.
  const managerTokenDeliveredRef = useRef(false);
  const connectionCleanupRef = useRef(() => {});

  // 6. Track authenticated user identity for pairing and account rebind.
  const authenticatedUserId = useSelector(userIdSelector);
  const authenticatedUserIdRef = useRef(authenticatedUserId);
  const managerApiTokenRef = useRef(managerApiToken);
  const connectToDesktopRef = useRef(null);
  const initializeManagerConnectionRef = useRef(null);
  authReadyRef.current = isAuthenticated;
  authenticatedUserIdRef.current = authenticatedUserId;
  managerApiTokenRef.current = managerApiToken;

  const clearManagerReadiness = useCallback(() => {
    initializedConnectionRef.current = null;
    initializationRequestedRef.current = null;
    pairingReadyRef.current = false;
    managerTokenDeliveredRef.current = false;
    dispatch(setManagerApiToken(''));
    dispatch(setManagerReady(false));
  }, [dispatch]);

  const markManagerReady = useCallback(
    (managerConnection) => {
      // Capability delivery gates readiness; SignalR transport alone is insufficient.
      if (
        activeConnection === managerConnection &&
        authReadyRef.current &&
        initializedConnectionRef.current === managerConnection &&
        pairingReadyRef.current &&
        managerTokenDeliveredRef.current
      ) {
        dispatch(setManagerReady(true));
      }
    },
    [dispatch]
  );

  const pairManagerIfNeeded = useCallback(
    async (status, userId) => {
      const currentToken = managerApiTokenRef.current;
      if (
        !status ||
        typeof status !== 'object' ||
        typeof status.deviceId !== 'string' ||
        status.deviceId.length === 0 ||
        (typeof currentToken === 'string' &&
          currentToken.length > 0 &&
          status.isPaired === true &&
          status.credentialAvailable === true &&
          typeof userId === 'number' &&
          userId > 0 &&
          status.currentUserId === userId)
      ) {
        return status;
      }
      if (pairingPromiseRef.current) return pairingPromiseRef.current;

      const pairingPromise = issueManagerPairingCode(status.deviceId)
        .then(({ code }) =>
          pairManager(code, status.deviceId).catch((error) => {
            if (error && typeof error === 'object')
              error.managerPairingStage = 'redeem';
            throw error;
          })
        )
        .then((pairingResponse) => {
          const typedPairingResponse =
            /** @type {{status?: unknown, managerApiToken?: unknown}} */ (
              pairingResponse
            );
          const pairingStatus = typedPairingResponse?.status;
          if (
            !isManagerPairingStatus(pairingStatus) ||
            pairingStatus.deviceId !== status.deviceId ||
            pairingStatus.isPaired !== true ||
            pairingStatus.credentialAvailable !== true ||
            typeof userId !== 'number' ||
            userId <= 0 ||
            pairingStatus.currentUserId !== userId
          )
            throw new Error('Manager pairing response was invalid');
          if (
            typeof typedPairingResponse?.managerApiToken !== 'string' ||
            typedPairingResponse.managerApiToken.length === 0
          )
            throw new Error('Manager pairing capability was invalid');
          dispatch(setManagerApiToken(typedPairingResponse.managerApiToken));
          return pairingStatus;
        })
        .catch((error) => {
          const responseStatus = error?.response?.status;
          const rejected =
            error?.managerPairingStage === 'redeem' &&
            (responseStatus === 400 ||
              responseStatus === 401 ||
              error?.code === 'AUTH_PAIRING_INVALID');
          logDebug(
            rejected
              ? 'GIMmanager pairing rejected: code invalid, expired, or wrong device'
              : 'GIMmanager pairing temporarily unavailable',
            responseStatus
          );
          if (
            rejected ||
            DEFINITIVE_MANAGER_FAILURES.includes(responseStatus)
          ) {
            clearManagerReadiness();
          }
          return null;
        })
        .finally(() => {
          pairingPromiseRef.current = null;
        });
      pairingPromiseRef.current = pairingPromise;
      return pairingPromise;
    },
    [clearManagerReadiness, dispatch]
  );

  const initializeManagerConnection = useCallback(
    async (managerConnection, refreshedConnectionId) => {
      // Reconnect refreshes transport identity, not readiness; valid pairing is
      // reused when status still matches, keeping recovery near-seamless.
      const connectionId =
        refreshedConnectionId ?? managerConnection?.connectionId;
      if (!connectionId) return null;
      if (
        initializationPromiseRef.current?.connection === managerConnection &&
        initializationPromiseRef.current.connectionId === connectionId
      ) {
        return initializationPromiseRef.current.promise;
      }

      // Status decides whether pairing is needed; only then issue one browser code.
      const initializationPromise = getManagerPairingStatus()
        .then(async (status) => {
          if (
            activeConnection !== managerConnection ||
            !authReadyRef.current ||
            managerConnection.connectionId !== connectionId
          ) {
            throw new Error('Manager status response was invalid');
          }
          if (!isManagerPairingStatus(status)) {
            clearManagerReadiness();
            throw new Error('Manager status response was invalid');
          }
          const userId = authenticatedUserIdRef.current;
          const currentToken = managerApiTokenRef.current;
          const needsPairing =
            pairingRequired(status, userId) ||
            typeof currentToken !== 'string' ||
            currentToken.length === 0;
          pairingReadyRef.current = !needsPairing;
          if (needsPairing) {
            clearManagerReadiness();
            const pairedStatus = await pairManagerIfNeeded(status, userId);
            if (!pairedStatus)
              throw new Error('Manager pairing did not complete');
            pairingReadyRef.current = true;
          }
          if (
            activeConnection !== managerConnection ||
            !authReadyRef.current ||
            managerConnection.connectionId !== connectionId
          )
            throw new Error('Manager connection changed during pairing');
          initializationRequestedRef.current = {
            connection: managerConnection,
            connectionId,
          };
          await initializeConnection(connectionId);
          logDebug('Requested Initializing with GIMmanager...');
          return status;
        })
        .catch((error) => {
          if (
            activeConnection === managerConnection &&
            managerConnection.connectionId === connectionId
          ) {
            initializedConnectionRef.current = null;
            initializationRequestedRef.current = null;
          }
          logDebug(
            'GIMmanager initialization failed; retrying',
            error?.response?.status
          );
          if (DEFINITIVE_MANAGER_FAILURES.includes(error?.response?.status))
            clearManagerReadiness();
          return null;
        })
        .finally(() => {
          if (
            initializationPromiseRef.current?.connection ===
              managerConnection &&
            initializationPromiseRef.current.connectionId === connectionId
          ) {
            initializationPromiseRef.current = null;
          }
        });
      initializationPromiseRef.current = {
        connection: managerConnection,
        connectionId,
        promise: initializationPromise,
      };
      return initializationPromise;
    },
    [clearManagerReadiness, pairManagerIfNeeded]
  );

  // Do not connect until browser auth is ready; manager events use the same gate.
  const connectToDesktop = useCallback(async () => {
    if (!authReadyRef.current) return null;
    if (
      activeConnection &&
      ['Connected', 'Connecting', 'Reconnecting'].includes(
        activeConnection.state
      )
    ) {
      return connectionStartPromise ?? Promise.resolve(activeConnection);
    }
    if (connectionStartPromise) return connectionStartPromise;

    const managerConnection = new HubConnectionBuilder()
      .withUrl(`${PLUGIN_HOST}/signalr`)
      .withAutomaticReconnect([5000, null])
      .build();
    activeConnection = managerConnection;

    const handlers = [];
    const register = (eventName, handler) => {
      managerConnection.on(eventName, handler);
      handlers.push([eventName, handler]);
    };
    const cleanupHandlers = () => {
      handlers.forEach(([eventName, handler]) =>
        managerConnection.off(eventName, handler)
      );
    };
    connectionCleanupRef.current = cleanupHandlers;

    // Compare instances so callbacks from a replaced connection cannot mutate state.
    managerConnection.onreconnecting(() => {
      if (activeConnection === managerConnection) {
        // Transport degradation is recoverable; retain valid capability/readiness.
        dispatch(setManagerTransportConnected(false));
      }
    });
    managerConnection.onreconnected((connectionId) => {
      if (activeConnection === managerConnection && authReadyRef.current) {
        initializationRequestedRef.current = null;
        dispatch(setManagerTransportConnected(true));
        // Refresh target connection id in background; status avoids needless re-pairing.
        void initializeManagerConnectionRef.current?.(
          managerConnection,
          connectionId
        );
      }
    });
    managerConnection.onclose(() => {
      if (activeConnection !== managerConnection) return;
      dispatch(setManagerTransportConnected(false));
      dispatch(setHideInstallButton(false));
      clearManagerReadiness();
      cleanupHandlers();
      if (activeConnection === managerConnection) activeConnection = null;
      if (mountedRef.current) setConnection(null);
    });

    register(
      GIMevents.PRO.OBJECT.UPLOAD_PROGRESS,
      (/** @type {ObjectUploadProgressEvent} */ data) => {
        if (authReadyRef.current) dispatch(UpdateObjectUploadProgress(data));
      }
    );
    register(
      GIMevents.PRO.OBJECT.DOWNLOAD_PROGRESS,
      (/** @type {ObjectDownloadProgressEvent} */ data) => {
        if (authReadyRef.current) dispatch(UpdateObjectDownloadProgress(data));
      }
    );
    register(GIMevents.PRO.OBJECT.UPLOAD_RESULT, (data) => {
      if (!authReadyRef.current) return;
      logTrace('got upload result event at HOOK!', data);
      dispatch(
        UpdateObjectUploadProgress({
          name: data.fileName,
          guid: data.guid,
          okay: data.okay,
          error: data.error,
          progress: 100,
          text: 'Completed!',
        })
      );
    });
    register(GIMevents.PRO.OBJECT.DOWNLOAD_RESULT, (data) => {
      if (!authReadyRef.current) return;
      logTrace('got download result event at HOOK!', data);
      dispatch(
        UpdateObjectDownloadProgress({
          name: data.fileName,
          guid: data.guid,
          okay: data.okay,
          error: data.error,
          progress: 100,
          text: 'Completed!',
        })
      );
    });
    register('SendtoJS_FilterFileList', (fileLists) => {
      if (authReadyRef.current) dispatch(updateStoreFileList(fileLists));
    });
    register(
      'SendtoJS_FileInitStatus',
      (projects, bSuccess, managerApiToken) => {
        if (activeConnection !== managerConnection || !authReadyRef.current)
          return;
        if (!bSuccess) {
          clearManagerReadiness();
          return;
        }
        dispatch(
          updateStoreGimProjects({
            ...projects,
            projectList: projects?.projectList,
          })
        );
        if (
          typeof managerApiToken !== 'string' ||
          managerApiToken.length === 0
        ) {
          clearManagerReadiness();
          return;
        }
        // Matching token delivery makes local capability usable; failures leave
        // readiness unset so the connection can be retried.
        const requestedInitialization = initializationRequestedRef.current;
        if (
          requestedInitialization?.connection !== managerConnection ||
          requestedInitialization.connectionId !==
            managerConnection.connectionId
        ) {
          return;
        }
        initializedConnectionRef.current = managerConnection;
        managerTokenDeliveredRef.current = true;
        dispatch(setManagerApiToken(managerApiToken));
        markManagerReady(managerConnection);
      }
    );

    let startPromise;
    startPromise = managerConnection
      .start()
      .then(() => {
        if (
          activeConnection !== managerConnection ||
          !authReadyRef.current ||
          !mountedRef.current
        ) {
          cleanupHandlers();
          return managerConnection
            .stop()
            .catch(() => {})
            .then(() => null);
        }
        setConnection(managerConnection);
        dispatch(setManagerTransportConnected(true));
        logDebug('Connected to GIMmanager');
        dispatch(getCurrentAppVersion()).catch(() => {});
        return managerConnection;
      })
      .catch(() => {
        cleanupHandlers();
        if (activeConnection === managerConnection) {
          activeConnection = null;
          dispatch(setManagerTransportConnected(false));
          clearManagerReadiness();
        }
        return null;
      })
      .finally(() => {
        if (connectionStartPromise === startPromise) {
          connectionStartPromise = null;
        }
      });
    connectionStartPromise = startPromise;

    return connectionStartPromise;
  }, [
    clearManagerReadiness,
    dispatch,
    initializeManagerConnection,
    markManagerReady,
  ]);
  connectToDesktopRef.current = connectToDesktop;
  initializeManagerConnectionRef.current = initializeManagerConnection;

  useEffect(() => {
    if (!managerReady) {
      dispatch(setHideInstallButton(false));
      return;
    }
    if (compareVersion(currentVersion, latestVersion))
      dispatch(setAppUpdateAvailable(true));
  }, [currentVersion, dispatch, latestVersion, managerReady]);

  useEffect(() => {
    mountedRef.current = true;
    if (!isAuthenticated) {
      dispatch(setManagerTransportConnected(false));
      clearManagerReadiness();
      return () => {
        mountedRef.current = false;
      };
    }

    dispatch(getLatestAppVersion()).catch(() => {});
    // Repeatedly retry so either manager-before-browser or browser-before-manager works.
    const ensureConnection = () => {
      connectToDesktopRef.current?.().then((connectedConnection) => {
        if (
          !connectedConnection ||
          initializedConnectionRef.current === connectedConnection
        ) {
          return;
        }
        void initializeManagerConnectionRef.current?.(connectedConnection);
      });
    };
    ensureConnection();
    retryIntervalRef.current = setInterval(
      ensureConnection,
      MANAGER_RETRY_INTERVAL_MS
    );

    return () => {
      mountedRef.current = false;
      if (retryIntervalRef.current) clearInterval(retryIntervalRef.current);
      retryIntervalRef.current = null;
      initializedConnectionRef.current = null;
      initializationRequestedRef.current = null;
      pairingReadyRef.current = false;
      managerTokenDeliveredRef.current = false;
      dispatch(setManagerTransportConnected(false));
      dispatch(setManagerReady(false));
      dispatch(setManagerApiToken(''));
      if (activeConnection) {
        const connectionToStop = activeConnection;
        // Remove handlers and stop before clearing ownership to avoid stale callbacks.
        connectionCleanupRef.current();
        void connectionToStop.stop().catch(() => {});
        connectionCleanupRef.current = () => {};
        setConnection(null);
        activeConnection = null;
        connectionStartPromise = null;
      }
    };
  }, [clearManagerReadiness, dispatch, authenticatedUserId, isAuthenticated]);

  return { managerReady, connection, connectToDesktop };
}
